import { useMutation, useQuery, useQueryClient } from "react-query";
import { ProjectSettingsApi } from "../api/projectSettings";
import { PrimeQueryKeys } from "../shared/constants";
import type { ApiProjectSettings } from "../shared/interfaces";
import { useTenantOrganizationId } from "../contexts/TenantIdentityContext";
import { resolveCacheApiBase } from "../contexts/TenantQueryClientProvider";
import { FEATURE_FLAG_STORAGE_KEY_PREFIX } from "./featureFlagStorageKey";

const FEATURE_FLAG_STALE_TIME = 5 * 60_000;

function readStoredFeatureFlags(storageKey: string | undefined): ApiProjectSettings | undefined {
    if (storageKey === undefined) return undefined;
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored === null) return undefined;

        const parsed: unknown = JSON.parse(stored);
        return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
            ? parsed
            : undefined;
    } catch {
        return undefined;
    }
}

function storeFeatureFlags(storageKey: string | undefined, features: ApiProjectSettings) {
    if (storageKey === undefined) return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(features));
    } catch {
        // A disabled or full localStorage must not turn a successful request into an error.
    }
}

const ENABLED_FEATURE_FLAG_VALUES = new Set(["1", "true", "yes", "on", "enabled"]);

/** Match ai_engine's feature-flag truth table for legacy/untyped KV values. */
export function featureFlagEnabled(value: unknown): boolean {
    if (typeof value === "boolean") return value;
    return ENABLED_FEATURE_FLAG_VALUES.has(String(value).trim().toLowerCase());
}

/**
 * Shared fetch behind every `features.*` project-setting hook: the tenant-scoped,
 * durably-snapshotted batch of raw `features.*` settings. Extracted from
 * `useFeatureFlag` so a caller that resolves its own key out of the same batch
 * (e.g. `useMarvinDefaultScopes`) doesn't duplicate the query key, storage-snapshot,
 * or staleness handling.
 */
export function useProjectFeatureSettings(): {
    features: ApiProjectSettings | undefined;
    isLoading: boolean;
    isAuthoritative: boolean;
    isError: boolean;
    /** The latest read has failed at least once, though it may still be retrying. */
    readFailed: boolean;
} {
    const organizationId = useTenantOrganizationId();
    // Scope the durable snapshot by tenant AND backend: under Capacitor the same
    // organization id exists on staging and production, and axios' baseURL is the
    // only thing that distinguishes them (mirrors the tenant query cache scope).
    // No resolvable base means no durable snapshot rather than an ambiguous one.
    const apiBase = resolveCacheApiBase();
    const storageKey =
        organizationId && apiBase
            ? `${FEATURE_FLAG_STORAGE_KEY_PREFIX}.${encodeURIComponent(organizationId)}--${encodeURIComponent(apiBase)}`
            : undefined;
    // The tenant QueryClient normally provides isolation. Including the identity here
    // also handles an in-place organization transition (notably embed auth) and ensures
    // an unscoped bootstrap request cannot seed a later tenant's query.
    const queryKey = PrimeQueryKeys.projectSettingsFeaturesForOrg(organizationId);
    // Keep the durable snapshot stale: it paints the correct flag immediately,
    // while every hard load still revalidates it against the server.
    const {
        data: features,
        isLoading,
        isFetching,
        isError,
        failureCount,
    } = useQuery(
        queryKey,
        async () => {
            const settings = await ProjectSettingsApi.getAllSettings("features.");
            storeFeatureFlags(storageKey, settings);
            return settings;
        },
        {
            enabled: organizationId !== undefined,
            staleTime: FEATURE_FLAG_STALE_TIME,
            initialData: () => readStoredFeatureFlags(storageKey),
            // React Query v3 treats an updated-at value of zero as missing data
            // during a refetch, which would bring the loading skeleton back.
            initialDataUpdatedAt: () => Date.now() - FEATURE_FLAG_STALE_TIME - 1,
        },
    );
    return {
        features,
        isLoading: organizationId === undefined || isLoading,
        // A durable snapshot paints immediately, but it must not authorize an
        // irreversible action until its background revalidation succeeds.
        isAuthoritative: organizationId !== undefined && !isFetching && !isError,
        isError,
        readFailed: isError || failureCount > 0,
    };
}

/**
 * Shared implementation for the `features.*` project-setting flags.
 * Fetches the batch of feature settings, exposes whether `flagKey` is enabled,
 * and provides a mutation to toggle it.
 */
export function useFeatureFlag(
    flagKey: string,
    defaultValue = false,
): {
    enabled: boolean;
    isLoading: boolean;
    isAuthoritative: boolean;
    /** The last read failed. Runtime hooks always provide this; optional for typed test doubles. */
    isError?: boolean;
    /** The latest read has failed at least once, though it may still be retrying. */
    readFailed?: boolean;
    toggle: (value: boolean) => void;
} {
    const qc = useQueryClient();
    const { features, isLoading, isAuthoritative, isError, readFailed } =
        useProjectFeatureSettings();
    const mutation = useMutation(
        (value: boolean) => ProjectSettingsApi.updateSetting(flagKey, value),
        { onSuccess: () => qc.invalidateQueries(PrimeQueryKeys.projectSettingsFeatures) },
    );
    return {
        // main's default-when-undefined, our truth table for defined values
        // (so "false"/"0"/"no" disable instead of being truthy).
        enabled:
            features?.[flagKey] === undefined
                ? defaultValue
                : featureFlagEnabled(features[flagKey]),
        isLoading,
        isAuthoritative,
        isError,
        readFailed,
        toggle: (value: boolean) => mutation.mutate(value),
    };
}
